import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import { SITE_PAGE_DEFINITIONS, sitePageContent } from '../lib/sitePages.js';
import { loadSiteSettings, saveSiteSettings } from '../lib/compat.js';
import { storage } from '../lib/firebase.js';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

async function uploadToStorage(file, path) {
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  return getDownloadURL(r);
}

function FieldLabel({ children }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>
      {children}
    </span>
  );
}

function FieldInput({ value, onChange, placeholder, style, readOnly }) {
  return (
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff', ...style }}
      onFocus={(e) => e.target.style.borderColor = 'var(--admin-navy)'}
      onBlur={(e) => e.target.style.borderColor = 'var(--admin-border)'}
    />
  );
}

export default function AdminSettings({ data, onRefresh, onSettingsChanged }) {
  const [form, setForm] = useState({
    siteName: data.settings?.siteName || 'Muppadai Training Academy',
    tagline: data.settings?.tagline || 'Government Exam Books',
    gaId: data.settings?.gaId || '',
    fbPixelId: data.settings?.fbPixelId || '',
    razorpayKey: data.settings?.razorpayKey || '',
    sitePages: data.settings?.sitePages || {},
    logoUrl: data.settings?.logoUrl || '',
    faviconUrl: data.settings?.faviconUrl || '',
    paymentWebhookUrl: Object.prototype.hasOwnProperty.call(data.settings || {}, 'paymentWebhookUrl') ? data.settings.paymentWebhookUrl || '' : 'https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjcwNTZmMDYzMjA0M2Q1MjZlNTUzNjUxMzMi_pc',
    shippedWebhookUrl: Object.prototype.hasOwnProperty.call(data.settings || {}, 'shippedWebhookUrl') ? data.settings.shippedWebhookUrl || '' : 'https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjcwNTZmMDYzMjA0M2Q1MjZlNTUzNjUxMzMi_pc'
  });
  const [logoPreview, setLogoPreview] = useState(form.logoUrl);
  const [favPreview, setFavPreview] = useState(form.faviconUrl);
  const [logoFile, setLogoFile] = useState(null);
  const [favFile, setFavFile] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [activePageSlug, setActivePageSlug] = useState(SITE_PAGE_DEFINITIONS[0].slug);

  // Load fresh settings on mount
  useEffect(() => {
    loadSiteSettings().then((s) => {
      if (!s) return;
      setForm((f) => ({ ...f, ...s }));
      setLogoPreview(s.logoUrl || '');
      setFavPreview(s.faviconUrl || '');
    }).catch(() => {});
  }, []);

  useEffect(() => { setLogoPreview(form.logoUrl); }, [form.logoUrl]);
  useEffect(() => { setFavPreview(form.faviconUrl); }, [form.faviconUrl]);

  function handleLogoFile(file) {
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }

  function handleFavFile(file) {
    if (!file) return;
    setFavFile(file);
    setFavPreview(URL.createObjectURL(file));
  }

  async function save() {
    setBusy(true);
    setMsg('');
    try {
      for (const definition of SITE_PAGE_DEFINITIONS) {
        const page = sitePageContent(form, definition.slug);
        if (!String(page.label || '').trim() || !String(page.heading || '').trim()) {
          throw new Error(`${definition.title}: footer label and page heading are required.`);
        }
        if (!page.sections.length || page.sections.some((section) => !String(section.heading || '').trim() || !String(section.body || '').trim())) {
          throw new Error(`${definition.title}: each section needs a heading and text.`);
        }
      }
      const contact = sitePageContent(form, 'contact-us');
      if (!contact.email && !contact.phone && !contact.address) throw new Error('Contact Us needs at least one contact detail.');
      if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) throw new Error('Enter a valid contact email address.');
      let logoUrl = form.logoUrl;
      let faviconUrl = form.faviconUrl;
      if (logoFile) logoUrl = await uploadToStorage(logoFile, `settings/logo_${Date.now()}_${logoFile.name}`);
      if (favFile) faviconUrl = await uploadToStorage(favFile, `settings/favicon_${Date.now()}_${favFile.name}`);
      const saved = await saveSiteSettings({ ...form, logoUrl, faviconUrl });
      setForm((f) => ({ ...f, logoUrl, faviconUrl }));
      setLogoFile(null);
      setFavFile(null);
      setMsg('✅ Settings saved successfully.');
      onSettingsChanged?.({ ...form, logoUrl, faviconUrl });
      await onRefresh();
    } catch (e) {
      setMsg('❌ Error: ' + (e.message || 'Unable to save settings.'));
    } finally {
      setBusy(false);
    }
  }

  const gaValid = !form.gaId || /^G-[A-Z0-9]+$/i.test(form.gaId);
  const activePage = sitePageContent(form, activePageSlug);

  function updatePage(change) {
    setForm((current) => {
      const page = sitePageContent(current, activePageSlug);
      return {
        ...current,
        sitePages: { ...current.sitePages, [activePageSlug]: change(page) }
      };
    });
  }

  function updateSection(index, field, value) {
    updatePage((page) => ({
      ...page,
      sections: page.sections.map((section, sectionIndex) => sectionIndex === index ? { ...section, [field]: value } : section)
    }));
  }

  return (
    <div style={{ maxWidth: 820 }}>
      {msg && (
        <div className={`banner compact`} style={{ marginBottom: 16, background: msg.startsWith('✅') ? '#e8f5ee' : '#FECDD3', color: msg.startsWith('✅') ? '#1a7a3e' : '#9B1C1C' }}>
          {msg}
        </div>
      )}

      {/* Logo + Favicon side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Logo */}
        <div className="adminCard" style={{ margin: 0 }}>
          <div className="adminCardHead"><h3>Logo</h3></div>
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 110, height: 64, borderRadius: 10, border: '1.5px solid var(--admin-border)', background: 'var(--admin-cream-dk)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, overflow: 'hidden', flexShrink: 0 }}>
                {logoPreview ? <img src={logoPreview} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={() => setLogoPreview('')} /> : '🎓'}
              </div>
              <p style={{ fontSize: 11, color: 'var(--admin-muted)', lineHeight: 1.6, margin: 0 }}>
                Recommended: PNG with transparent background, 200×60px minimum
              </p>
            </div>
            <div>
              <FieldLabel>Upload Logo</FieldLabel>
              <input type="file" accept="image/*" onChange={(e) => handleLogoFile(e.target.files?.[0])} style={{ fontSize: 12, display: 'block' }} />
            </div>
            <div>
              <FieldLabel>Or Image URL</FieldLabel>
              <FieldInput value={form.logoUrl} placeholder="https://…" onChange={(e) => { setForm({ ...form, logoUrl: e.target.value }); setLogoPreview(e.target.value); }} />
            </div>
          </div>
        </div>

        {/* Favicon */}
        <div className="adminCard" style={{ margin: 0 }}>
          <div className="adminCardHead"><h3>Favicon</h3></div>
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: 10, border: '1.5px solid var(--admin-border)', background: 'var(--admin-cream-dk)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, overflow: 'hidden', flexShrink: 0 }}>
                {favPreview ? <img src={favPreview} alt="Favicon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => setFavPreview('')} /> : '🌐'}
              </div>
              <p style={{ fontSize: 11, color: 'var(--admin-muted)', lineHeight: 1.6, margin: 0 }}>
                ICO or PNG, 32×32 or 64×64px recommended. Applied in browser tab.
              </p>
            </div>
            <div>
              <FieldLabel>Upload Favicon</FieldLabel>
              <input type="file" accept="image/*,.ico" onChange={(e) => handleFavFile(e.target.files?.[0])} style={{ fontSize: 12, display: 'block' }} />
            </div>
            <div>
              <FieldLabel>Or Image URL</FieldLabel>
              <FieldInput value={form.faviconUrl} placeholder="https://…" onChange={(e) => { setForm({ ...form, faviconUrl: e.target.value }); setFavPreview(e.target.value); }} />
            </div>
          </div>
        </div>
      </div>

      {/* Site Information */}
      <div className="adminCard" style={{ marginBottom: 16 }}>
        <div className="adminCardHead"><h3>Site Information</h3></div>
        <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <FieldLabel>Academy Name</FieldLabel>
            <FieldInput value={form.siteName} onChange={(e) => setForm({ ...form, siteName: e.target.value })} placeholder="Muppadai Training Academy" />
          </div>
          <div>
            <FieldLabel>Tagline</FieldLabel>
            <FieldInput value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Government Exam Books" />
          </div>
        </div>
      </div>

      {/* Tracking & Analytics */}
      <div className="adminCard" style={{ marginBottom: 20 }}>
        <div className="adminCardHead"><h3>Tracking &amp; Analytics</h3></div>
        <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <FieldLabel>GA4 Measurement ID</FieldLabel>
            <FieldInput
              value={form.gaId}
              placeholder="G-XXXXXXXXXX"
              style={{ fontFamily: 'monospace', borderColor: form.gaId && !gaValid ? '#B91C1C' : undefined }}
              onChange={(e) => setForm({ ...form, gaId: e.target.value.trim() })}
            />
            {form.gaId && !gaValid && <span style={{ fontSize: 11, color: '#B91C1C', marginTop: 4, display: 'block' }}>Should look like G-XXXXXXXXXX</span>}
            {form.gaId && gaValid && <span style={{ fontSize: 11, color: 'var(--admin-green)', marginTop: 4, display: 'block' }}>✓ Valid GA4 ID</span>}
          </div>
          <div>
            <FieldLabel>Facebook Pixel ID</FieldLabel>
            <FieldInput
              value={form.fbPixelId}
              placeholder="1234567890123"
              style={{ fontFamily: 'monospace' }}
              onChange={(e) => setForm({ ...form, fbPixelId: e.target.value.trim() })}
            />
            {form.fbPixelId && <span style={{ fontSize: 11, color: 'var(--admin-green)', marginTop: 4, display: 'block' }}>✓ FB Pixel set</span>}
          </div>
        </div>
      </div>

      <div className="adminCard" style={{ marginBottom: 20 }}>
        <div className="adminCardHead">
          <h3>Payment Gateway</h3>
          <span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>Razorpay checkout Key ID only</span>
        </div>
        <div style={{ padding: '18px 20px', display: 'grid', gap: 16 }}>
          <div>
            <FieldLabel>Razorpay Key ID</FieldLabel>
            <FieldInput
              value={form.razorpayKey}
              placeholder="rzp_live_..."
              style={{ fontFamily: 'monospace' }}
              onChange={(e) => setForm({ ...form, razorpayKey: e.target.value.trim() })}
            />
            <span style={{ fontSize: 11, color: 'var(--admin-muted)', marginTop: 5, display: 'block' }}>
              Paste only the Razorpay Key ID. Never paste the Key Secret in this website or admin panel.
            </span>
          </div>
        </div>
      </div>

      <div className="adminCard" style={{ marginBottom: 20 }}>
        <div className="adminCardHead">
          <h3>Webhook Automation</h3>
          <span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>Payment uses default if blank. Shipped can be left blank.</span>
        </div>
        <div style={{ padding: '18px 20px', display: 'grid', gap: 16 }}>
          <div>
            <FieldLabel>Payment Success Webhook</FieldLabel>
            <FieldInput
              value={form.paymentWebhookUrl}
              placeholder="https://connect.pabbly.com/workflow/sendwebhookdata/..."
              style={{ fontFamily: 'monospace' }}
              onChange={(e) => setForm({ ...form, paymentWebhookUrl: e.target.value.trim() })}
            />
            <span style={{ fontSize: 11, color: 'var(--admin-muted)', marginTop: 5, display: 'block' }}>
              Sends when Razorpay payment succeeds or a manual paid order is created. If blank, the original Pabbly payment webhook is used.
            </span>
          </div>
          <div>
            <FieldLabel>Shipped Notification Webhook</FieldLabel>
            <FieldInput
              value={form.shippedWebhookUrl}
              placeholder="Paste approved WhatsApp shipped workflow webhook URL"
              style={{ fontFamily: 'monospace' }}
              onChange={(e) => setForm({ ...form, shippedWebhookUrl: e.target.value.trim() })}
            />
            <span style={{ fontSize: 11, color: 'var(--admin-muted)', marginTop: 5, display: 'block' }}>
              Sends when admin saves a Professional Courier tracking number. Use this for the approved WhatsApp shipped template.
            </span>
          </div>
        </div>
      </div>

      <div className="adminCard" style={{ marginBottom: 20 }}>
        <div className="adminCardHead"><h3>Footer Pages</h3></div>
        <div style={{ padding: '18px 20px' }}>
          <div role="tablist" aria-label="Footer pages" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {SITE_PAGE_DEFINITIONS.map((page) => (
              <button
                key={page.slug}
                type="button"
                role="tab"
                aria-selected={activePageSlug === page.slug}
                onClick={() => setActivePageSlug(page.slug)}
                style={{ padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 6, background: activePageSlug === page.slug ? 'var(--admin-navy)' : '#fff', color: activePageSlug === page.slug ? '#fff' : 'var(--admin-text)', fontSize: 12, fontWeight: 700 }}
              >
                {page.title}
              </button>
            ))}
          </div>
          <div role="tabpanel" style={{ display: 'grid', gap: 16 }}>
            <div>
              <FieldLabel>Footer Link Label</FieldLabel>
              <FieldInput value={activePage.label} onChange={(e) => updatePage((page) => ({ ...page, label: e.target.value }))} />
            </div>
            <div>
              <FieldLabel>Page Heading</FieldLabel>
              <FieldInput value={activePage.heading} onChange={(e) => updatePage((page) => ({ ...page, heading: e.target.value }))} />
            </div>
            {activePageSlug === 'contact-us' && (
              <>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  <FieldInput value={activePage.email || ''} placeholder="info@example.com" onChange={(e) => updatePage((page) => ({ ...page, email: e.target.value }))} />
                </div>
                <div>
                  <FieldLabel>Phone / WhatsApp</FieldLabel>
                  <FieldInput value={activePage.phone || ''} placeholder="+91 ..." onChange={(e) => updatePage((page) => ({ ...page, phone: e.target.value }))} />
                </div>
                <div>
                  <FieldLabel>Postal Address</FieldLabel>
                  <textarea value={activePage.address || ''} rows={3} onChange={(e) => updatePage((page) => ({ ...page, address: e.target.value }))} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, resize: 'vertical', fontSize: 13 }} />
                </div>
              </>
            )}
            {activePage.sections.map((section, index) => (
              <div key={`${activePageSlug}-${index}`} style={{ borderTop: '1px solid var(--admin-border)', paddingTop: 16, display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <FieldLabel>Section Heading</FieldLabel>
                    <FieldInput value={section.heading} onChange={(e) => updateSection(index, 'heading', e.target.value)} />
                  </div>
                  <button type="button" title="Remove section" aria-label={`Remove section ${index + 1}`} onClick={() => updatePage((page) => ({ ...page, sections: page.sections.filter((_, sectionIndex) => sectionIndex !== index) }))} style={{ width: 36, height: 36, border: '1px solid var(--admin-border)', borderRadius: 6, background: '#fff', color: '#B91C1C', display: 'grid', placeItems: 'center' }}><Trash2 size={16} /></button>
                </div>
                <div>
                  <FieldLabel>Section Text</FieldLabel>
                  <textarea value={section.body} rows={4} onChange={(e) => updateSection(index, 'body', e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, resize: 'vertical', fontSize: 13, lineHeight: 1.5 }} />
                </div>
              </div>
            ))}
            <button type="button" onClick={() => updatePage((page) => ({ ...page, sections: [...page.sections, { heading: '', body: '' }] }))} style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 6, background: '#fff', fontSize: 12, fontWeight: 700 }}><Plus size={15} /> Add Section</button>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="btn-primary"
        onClick={save}
        disabled={busy}
        style={{ padding: '13px 32px', fontSize: 14, width: '100%', justifyContent: 'center', borderRadius: 10 }}
      >
        <Save size={16} /> {busy ? 'Saving…' : 'Save All Settings'}
      </button>
    </div>
  );
}
